import React from 'react';
import styled from 'styled-components';
import { User, Team } from '../types';
import { UserAvatar } from './UserAvatar';

interface CallsRowProps {
  calls: Team[];
  getUsersForTeam: (teamId: string) => User[];
  workspaceTeamId?: string;
}

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 16px;
  max-width: 1400px;
  margin: 0 auto 30px;

  @media (min-width: 768px) {
    gap: 20px;
    margin-bottom: 40px;
  }
`;

// Sized to its people instead of a full grid cell
const CallCard = styled.div<{ $isZoom: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-width: 100%;
  padding: 14px 18px;
  border-radius: 16px;
  background: ${props => props.$isZoom ? 'rgba(45, 140, 240, 0.12)' : 'rgba(33, 150, 243, 0.1)'};
  border: 1px solid ${props => props.$isZoom ? 'rgba(45, 140, 240, 0.35)' : 'rgba(33, 150, 243, 0.3)'};
  box-shadow: 0 4px 16px ${props => props.$isZoom ? 'rgba(45, 140, 240, 0.15)' : 'rgba(33, 150, 243, 0.15)'};
  backdrop-filter: blur(10px);
`;

const CallHeader = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`;

const CallName = styled.span`
  color: #ffffff;
  font-size: 0.95rem;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 320px;
`;

const Pill = styled.span`
  padding: 2px 10px;
  font-size: 0.72rem;
  font-weight: 500;
  color: white;
  border-radius: 12px;
  white-space: nowrap;
`;

const GuestBadge = styled(Pill)`
  background: rgba(240, 160, 45, 0.7);
`;

const JoinLink = styled(Pill).attrs({ as: 'a' })`
  background: rgba(45, 140, 240, 0.6);
  text-decoration: none;
  transition: background 0.2s ease;

  &:hover {
    background: rgba(45, 140, 240, 0.9);
  }
`;

const People = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

export const CallsRow: React.FC<CallsRowProps> = ({ calls, getUsersForTeam, workspaceTeamId }) => {
  if (calls.length === 0) return null;

  return (
    <Row>
      {calls.map(call => {
        const isZoom = call.id.startsWith('zoom-');
        const people = getUsersForTeam(call.id);

        return (
          <CallCard key={call.id} $isZoom={isZoom}>
            <CallHeader>
              <CallName title={call.name}>{call.name}</CallName>
              {!!call.externalCount && (
                <GuestBadge title="Someone from outside Chatbooks is in this meeting">
                  🤝 {call.externalCount} external {call.externalCount === 1 ? 'guest' : 'guests'}
                </GuestBadge>
              )}
              {isZoom && call.joinUrl && (
                <JoinLink href={call.joinUrl} target="_blank" rel="noopener noreferrer">
                  Join
                </JoinLink>
              )}
            </CallHeader>
            <People>
              {people.map(user => (
                <UserAvatar key={user.id} user={user} workspaceTeamId={workspaceTeamId} />
              ))}
            </People>
          </CallCard>
        );
      })}
    </Row>
  );
};
